<template>
  <b-overlay :show="isLoading" rounded="sm">
    <template #overlay>
      <div class="text-center">
        <digger-loader />
      </div>
    </template>

    <div class="p-relative header-image pb-5"></div>

    <b-container class="custom-container mt-5">
      <b-row>
        <b-col cols="12" class="px-3">
          <candidates-tabs />

          <div
            class="d-flex flex-wrap align-items-center justify-content-between pb-1"
          >
            <h4 class="font-weight-bold mb-0">Job Postings</h4>
            <b-button
              v-if="hasPermission('jobs.create')"
              variant="primary"
              class="post-job-btn"
              :to="{ name: 'jobs.new-page' }"
            >
              Post a job
            </b-button>
          </div>
          <p class="text-muted mb-4">
            Advertise work to tradies and review who has applied.
          </p>

          <b-nav tabs class="jobs-list-tabs mb-3">
            <b-nav-item
              :active="tab === 'jobs'"
              data-test="tab-jobs"
              @click="tab = 'jobs'"
            >
              Jobs
            </b-nav-item>
            <b-nav-item
              :active="tab === 'not-suited'"
              data-test="tab-not-suited"
              @click="tab = 'not-suited'"
            >
              Not suited
            </b-nav-item>
          </b-nav>

          <not-suited-list
            v-if="tab === 'not-suited'"
            :can-manage="hasPermission('jobs.edit')"
          />

          <div v-show="tab === 'jobs'">
            <job-insights :tiles="insightTiles" />

            <b-row class="job-filters">
              <b-col md="6" class="mb-3">
                <b-form-input
                  v-model="search"
                  type="search"
                  placeholder="Search jobs by title"
                  class="job-search"
                  @input="onSearchInput"
                  @keyup.enter="onSearchNow"
                />
              </b-col>
              <b-col md="4" class="mb-3">
                <b-form-select
                  v-model="status"
                  :options="statusOptions"
                  class="job-status-select"
                  @change="onFilterChanged"
                />
              </b-col>
              <b-col md="2" class="mb-3">
                <b-button
                  variant="outline-secondary"
                  class="bg-white text-dark clear-filters-btn"
                  :disabled="!hasFilters"
                  @click="onClearFilters"
                >
                  Clear
                </b-button>
              </b-col>
            </b-row>

            <template v-if="jobs.length">
              <!-- Desktop / tablet table -->
              <b-table
                :items="jobs"
                :fields="fields"
                responsive
                hover
                class="d-none d-md-table job-table"
                tbody-tr-class="job-row"
                @row-clicked="goToJob"
              >
                <template #cell(title)="{ item }">
                  <span class="job-title">{{ item.title }}</span>
                </template>
                <template #cell(trade)="{ item }">
                  {{ item.trade ? item.trade.name : '-' }}
                </template>
                <template #cell(location)="{ item }">
                  {{ locationLabel(item) }}
                </template>
                <template #cell(status)="{ item }">
                  <job-status-badge :job="item" />
                </template>
                <template #cell(applications_count)="{ item }">
                  {{ item.applications_count || 0 }}
                </template>
                <template #cell(created_at)="{ item }">
                  {{ formatDate(item.created_at) }}
                </template>
              </b-table>

              <!-- Phones: stacked cards -->
              <div class="d-md-none">
                <div
                  v-for="job in jobs"
                  :key="job.id"
                  class="job-card mb-3"
                  role="button"
                  tabindex="0"
                  @click="goToJob(job)"
                  @keyup.enter="goToJob(job)"
                >
                  <div class="d-flex justify-content-between align-items-start">
                    <span class="job-title">{{ job.title }}</span>
                    <job-status-badge :job="job" class="ml-2" />
                  </div>
                  <div class="job-card-meta">
                    {{ job.trade ? job.trade.name : 'No trade' }}
                    &middot; {{ locationLabel(job) }}
                  </div>
                  <div class="job-card-meta">
                    {{ job.applications_count || 0 }} applicant{{
                      job.applications_count === 1 ? '' : 's'
                    }}
                    &middot; Posted {{ formatDate(job.created_at) }}
                  </div>
                </div>
              </div>
            </template>

            <p v-else-if="!isLoading" class="text-muted py-5 text-center">
              {{ emptyMessage }}
            </p>

            <b-pagination
              v-if="jobsMeta && jobsMeta.total > perPage"
              v-model="currentPage"
              :total-rows="jobsMeta.total"
              :per-page="perPage"
              align="center"
              class="mt-3"
              @change="onPageChanged"
            />
          </div>
        </b-col>
      </b-row>
    </b-container>
  </b-overlay>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import CandidatesTabs from '@/components/candidates/CandidatesTabs.vue'
import JobInsights from '@/components/jobPostings/JobInsights.vue'
import JobStatusBadge from '@/components/jobPostings/JobStatusBadge.vue'
import NotSuitedList from '@/components/jobPostings/NotSuitedList.vue'
import apiToasts from '@/mixins/apiToasts'
import { STATUS_FILTERS, formatDate, locationLabel } from '@/utils/jobPostings'

export default {
  name: 'JobsListPage',

  components: { CandidatesTabs, JobInsights, JobStatusBadge, NotSuitedList },

  mixins: [apiToasts],

  data() {
    // Reopen with the filters and page from the last visit, so going into a job
    // and back does not reset the list. The store holds them for the session.
    const saved = this.$store.state.jobPosting
    const filters = saved.filters || {}
    return {
      isLoading: false,
      // 'jobs' | 'not-suited'
      tab: 'jobs',
      status: filters.status || '',
      search: filters.q || '',
      currentPage: saved.page || 1,
      perPage: 15,
      statusOptions: STATUS_FILTERS,
      searchTimer: null,
      fields: [
        { key: 'title', label: 'Job Title' },
        { key: 'trade', label: 'Trade' },
        { key: 'location', label: 'Location' },
        { key: 'status', label: 'Status' },
        { key: 'applications_count', label: 'Applicants' },
        { key: 'created_at', label: 'Date Posted' },
      ],
    }
  },

  computed: {
    ...mapGetters('jobPosting', ['jobs', 'jobsMeta', 'insights']),
    ...mapGetters('auth', ['hasPermission']),

    // Hidden until the numbers arrive, so the page does not flash zeros.
    insightTiles() {
      const i = this.insights
      if (!i) return []
      return [
        {
          key: 'live',
          label: 'Live Jobs',
          value: i.live_jobs,
          icon: 'broadcast',
        },
        {
          key: 'applicants',
          label: 'Total Applicants',
          value: i.total_applicants,
          icon: 'people-fill',
        },
        {
          key: 'new',
          label: 'New Applicants to Review',
          value: i.new_applicants,
          icon: 'person-plus-fill',
        },
        {
          key: 'shortlisted',
          label: 'Shortlisted',
          value: i.shortlisted,
          icon: 'star-fill',
        },
        {
          key: 'expiring',
          label: 'Expiring in 7 Days',
          value: i.expiring_soon,
          icon: 'clock-history',
        },
        {
          key: 'draft',
          label: 'Drafts',
          value: i.draft_jobs,
          icon: 'pencil-square',
        },
      ]
    },

    hasFilters() {
      return !!(this.search.trim() || this.status)
    },

    emptyMessage() {
      if (this.search || this.status) return 'No jobs match these filters.'
      return 'You have not posted any jobs yet.'
    },
  },

  async created() {
    // Numbers are a nice-to-have; a failure must not block the list.
    this.fetchInsights().catch(() => {})
    await this.loadJobs()
  },

  beforeDestroy() {
    clearTimeout(this.searchTimer)
  },

  methods: {
    ...mapActions('jobPosting', ['fetchJobs', 'fetchInsights']),

    formatDate,
    locationLabel,

    async loadJobs() {
      this.isLoading = true
      try {
        await this.fetchJobs({
          filters: { status: this.status, q: this.search.trim() },
          page: this.currentPage,
          perPage: this.perPage,
        })
      } catch (err) {
        this.onError(err)
      } finally {
        this.isLoading = false
      }
    },

    onClearFilters() {
      clearTimeout(this.searchTimer)
      this.search = ''
      this.status = ''
      this.onFilterChanged()
    },

    onFilterChanged() {
      this.currentPage = 1
      this.loadJobs()
    },

    // Wait for a pause in typing so each keystroke is not a request. Emptying
    // the box is a deliberate "show everything", so that goes straight through.
    onSearchInput() {
      clearTimeout(this.searchTimer)
      if (!this.search.trim()) return this.onFilterChanged()
      this.searchTimer = setTimeout(this.onFilterChanged, 500)
    },

    // Enter skips the wait.
    onSearchNow() {
      clearTimeout(this.searchTimer)
      this.onFilterChanged()
    },

    onPageChanged(page) {
      this.currentPage = page
      this.loadJobs()
    },

    goToJob(job) {
      this.$router.push({
        name: 'jobs.view-job',
        params: { jobId: job.id },
      })
    },
  },
}
</script>

<style lang="scss" scoped>
// Gray values follow the newer Tailwind-style scale in DESIGN.md.
.job-title {
  font-weight: 600;
  color: #111827;
}

// Same tab treatment as the project page tabs: gray-500 idle, gray-900 active.
.jobs-list-tabs {
  border-bottom-color: #e5e7eb;

  /deep/ .nav-link {
    color: #6b7280;
    font-weight: 600;

    &.active {
      color: #111827;
    }
  }
}

.job-table /deep/ .job-row {
  cursor: pointer;
}

.job-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 1rem;
  cursor: pointer;
}

.job-card-meta {
  margin-top: 0.25rem;
  font-size: 0.875rem;
  color: #6b7280;
}

// Match the 50px search and select beside it (the global .btn is 56px).
.clear-filters-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 50px;
  padding: 0 16px;
  font-weight: 600;
}

// The global .btn is a fixed 56px tall with 4px vertical padding, which pins
// the label to the top of a block button. Centre it and size the button to
// the heading beside it.
.post-job-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 44px;
  min-width: 140px;
  padding: 0 24px;
  font-weight: 600;
}
</style>
